import { paths } from "../../routes/paths";

/**
 * Find campaign configuration by type
 * @param {Array} campaignsConfiguration - Available campaign configurations
 * @param {string} campaignType - Campaign type to find
 * @returns {Object|null} - Found campaign configuration or null
 */
export const findCampaignConfig = (campaignsConfiguration, campaignType) => {
  return campaignsConfiguration?.find((c) => c?.key === campaignType);
};

/**
 * Handle navigating to campaign summary view
 * @param {Object} campaign - Campaign object
 * @param {Array} campaignsConfiguration - Available campaign configurations
 * @param {Function} setCurrentCampaignTypeConfig - Function to set current campaign type config
 * @param {Function} navigate - React router navigate function
 */
export const handleSummaryNavigation = (
  campaign,
  campaignsConfiguration,
  setCurrentCampaignTypeConfig,
  navigate
) => {
  if (!campaign?.id || !campaign?.type) return;

  const currentCampaignTypeConfiguration = findCampaignConfig(
    campaignsConfiguration,
    campaign?.type
  );

  if (currentCampaignTypeConfiguration?.value) {
    setCurrentCampaignTypeConfig({
      ...currentCampaignTypeConfiguration?.value,
      key: currentCampaignTypeConfiguration.key,
    });
    navigate(`/${paths.campaigns}/${paths.campaign_summary_view}/${campaign.id}/`);
  }
};

/**
 * Handle navigating to edit campaign view
 * @param {Object} campaign - Campaign object
 * @param {Array} campaignsConfiguration - Available campaign configurations
 * @param {Function} setCurrentCampaignTypeConfig - Function to set current campaign type config
 * @param {Function} navigate - React router navigate function
 */
export const handleEditNavigation = (
  campaign,
  campaignsConfiguration,
  setCurrentCampaignTypeConfig,
  navigate
) => {
  if (!campaign?.id || !campaign?.type) return;

  const currentCampaignTypeConfiguration = findCampaignConfig(
    campaignsConfiguration,
    campaign?.type
  );

  if (currentCampaignTypeConfiguration?.value) {
    setCurrentCampaignTypeConfig({
      ...currentCampaignTypeConfiguration?.value,
      key: currentCampaignTypeConfiguration.key,
    });
    navigate(
      `/${paths.campaigns}/${paths.edit}/${campaign.id}/${currentCampaignTypeConfiguration?.value?.steps[0]?.slug || paths.generic_setup}`
    );
  }
};

/**
 * Process campaign data for copying
 * @param {Object} campaignData - Original campaign data
 * @returns {Object} - Processed campaign data for copying
 */
export const processCampaignDataForCopy = (campaignData) => {
  if (!campaignData) return null;

  const loaddata = JSON.parse(JSON.stringify(campaignData));
  delete loaddata.__typename;
  delete loaddata.approvalStatus;
  delete loaddata.archivedAt;
  delete loaddata.campaignStatus;
  delete loaddata.paused;
  delete loaddata.publishingStatus;
  loaddata.campaignContent = { ...loaddata.campaignContent, id: null };
  loaddata.id = crypto.randomUUID();
  loaddata.title = `${loaddata.title} copy`;

  if (loaddata?.campaignContent?.contentData?.title) {
    loaddata.campaignContent.contentData.title = `${loaddata?.campaignContent?.contentData?.title} copy`;
  }
  if (loaddata?.campaignContent?.contentData?.title_short) {
    loaddata.campaignContent.contentData.title_short = `${loaddata?.campaignContent?.contentData?.title_short} copy`;
  }
  if (loaddata?.campaignContent?.slug) {
    delete loaddata.campaignContent.slug;
  }

  return loaddata;
};

/**
 * Create a new bonus campaign
 * @param {Array} campaignsConfiguration - Available campaign configurations
 * @param {Function} setCurrentCampaignTypeConfig - Function to set current campaign type config
 * @param {Function} setCampaignPresets - Function to set campaign presets
 * @param {Function} navigate - React router navigate function
 */
export const createBonusNavigation = (
  campaignsConfiguration,
  setCurrentCampaignTypeConfig,
  setCampaignPresets,
  navigate
) => {
  const currentCampaignTypeConfiguration = findCampaignConfig(
    campaignsConfiguration,
    "gift_processing"
  );

  if (currentCampaignTypeConfiguration?.value) {
    setCurrentCampaignTypeConfig({
      ...currentCampaignTypeConfiguration?.value,
      key: "gift_processing",
    });
    setCampaignPresets({
      ...currentCampaignTypeConfiguration?.value,
      type: "gift_processing",
    });
    navigate(
      `/${paths.campaigns}/${paths.create}/${currentCampaignTypeConfiguration?.value?.steps[0]?.slug || paths.generic_setup}`
    );
  }
};

/**
 * Renders campaigns based on view type
 * @param {boolean} loading - Whether campaigns are being loaded
 * @param {Array} campaigns - List of campaigns
 * @param {string} viewType - Type of view ('grid' or 'list')
 * @param {string} programTimezone - Program timezone
 * @param {Function} handleSummaryClick - Function to handle summary click action
 * @param {Function} hasCreateCampaignAccess - Whether user has access to create campaign
 * @param {Function} isBonusFlowEnabled - Whether bonus flow is enabled
 * @param {string} programName - Program name
 * @param {string} EmptyImage - URL for empty state image
 * @param {Function} Grid - Grid component
 * @param {Function} BonusGridCard - Bonus grid card component
 * @param {Function} BonusListView - Bonus list view component
 * @param {Function} LoadingSpinner - Loading spinner component
 * @param {Function} getCampaignActionButtons - Function to get campaign action buttons
 * @returns {JSX.Element} - The rendered campaigns
 */
export const renderCampaignsUtil = (
  loading,
  campaigns,
  viewType,
  programTimezone,
  handleSummaryClick,
  hasCreateCampaignAccess,
  isBonusFlowEnabled,
  programName,
  EmptyImage,
  Grid,
  BonusGridCard,
  BonusListView,
  LoadingSpinner,
  getCampaignActionButtons
) => {
  if (loading) {
    return (
      <div className="loading-container" data-testid="utils-loading-spinner">
        <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
      </div>
    );
  }

  if (!loading && campaigns?.length === 0) {
    return (
      <div className="empty-state">
        <img src={EmptyImage} alt="No campaigns created" />
        <p>No campaigns created yet</p>
      </div>
    );
  }

  return viewType === "grid" ? (
    <Grid container spacing={2}>
      {campaigns?.map(
        (campaign) =>
          campaign && (
            <Grid key={campaign.id} item xs={6} md={4} lg={3}>
              <BonusGridCard
                campaign={campaign}
                programTimezone={programTimezone}
                actionButtons={getCampaignActionButtons?.({
                  status: campaign?.status,
                  campaign: campaign,
                  handleSummaryClick,
                  hasAccess: hasCreateCampaignAccess,
                  isBonusFlowEnabled,
                })}
              />
            </Grid>
          )
      )}
    </Grid>
  ) : (
    <BonusListView
      campaignData={campaigns}
      programSlug={programName}
      handleSummaryClick={handleSummaryClick}
      hasAccess={hasCreateCampaignAccess}
    />
  );
};
